import { useState, useEffect } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import { fetchData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { useSessionValidation } from '../../../utils/sessionValidation';
import Button from '@mui/material/Button';
import { CircularProgress, Fade, Tooltip, Chip, LinearProgress } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';

const UserEdit = () => {
    useSessionValidation();

    const { id } = useParams();
    const history = useHistory();
    const { isAuthenticated, companyId, userId, logout, token } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [mounted, setMounted] = useState(false);
    const [formProgress, setFormProgress] = useState(0);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const [formData, setFormData] = useState({
        FirstName: '',
        LastName: '',
        UserName: '',
        Email: '',
        ContactNumber: '',
        isSubmit: false
    });

    useEffect(() => {
        setMounted(true);
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
    }, [isAuthenticated, companyId]);

    useEffect(() => {
        fetchData('UserApi', `/UserDetails/${id}`, token)
            .then((response) => {
                if (response.responseCode === '1111') {
                    setFormData(response.response_data);
                }
            })
            .catch((error) => {
                console.error('Error fetching User Details:', error);
            });
    }, [id]);

    useEffect(() => {
        const { FirstName, LastName, ContactNumber, Email } = formData;
        const requiredFields = [FirstName, LastName, ContactNumber, Email];
        const filled = requiredFields.filter(f => f && f.trim() !== '').length;
        setFormProgress(Math.round((filled / requiredFields.length) * 100));
    }, [formData]);

    const handleChange = (event) => {
        const { name, value, type } = event.target;
        setFormData((prev) => {
            const updated = { ...prev, [name]: type === 'checkbox' ? !prev[name] : value };
            if (name === 'FirstName' || name === 'LastName') {
                const fn = name === 'FirstName' ? value : prev.FirstName;
                const ln = name === 'LastName' ? value : prev.LastName;
                updated.UserName = `${fn.toLowerCase()}_${ln.toLowerCase()}`.slice(0, 13);
            }
            return updated;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.FirstName) { setErrorMessage('First Name is required!'); return; }
        if (!formData.LastName) { setErrorMessage('Last Name is required!'); return; }
        if (!formData.ContactNumber) { setErrorMessage('Contact Number is required!'); return; }
        if (!formData.Email) { setErrorMessage('Email is required!'); return; }

        setIsLoading(true);
        setErrorMessage('');
        try {
            const payload = { ...formData, UpdatedBy: userId };
            await updateData('UserApi', `${id}`, payload, token);
            setSuccessMessage('User updated successfully!');
            setTimeout(() => history.push('/UserIndex'), 1500);
        } catch (error) {
            console.error('Error updating record:', error);
            setErrorMessage('Failed to update user.');
            setIsLoading(false);
        }
    };

    return (
        <Fade in={mounted} timeout={800}>
            <div style={{ minWidth: '100%', background: '#f5f7fa', padding: '10px', position: 'relative' }}>
                <style>{`
                    @keyframes slideIn {
                        from { opacity: 0; transform: translateY(20px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    .input-field-edit { transition: all 0.3s ease; border: 2px solid transparent; }
                    .input-field-edit:focus {
                        border-color: #667eea;
                        box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
                        transform: translateY(-2px);
                    }
                    .input-field-edit:hover { border-color: #e0e0e0; }
                    .action-button-edit {
                        transition: all 0.3s ease;
                        text-transform: none;
                        font-weight: 600;
                        letter-spacing: 0.5px;
                    }
                    .action-button-edit:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
                    }
                    .section-card-edit {
                        animation: slideIn 0.6s ease-out forwards;
                        opacity: 0;
                    }
                    .section-card-edit:nth-child(1) { animation-delay: 0.1s; }
                    .section-card-edit:nth-child(2) { animation-delay: 0.2s; }
                    @keyframes shimmer {
                        0% { background-position: -1000px 0; }
                        100% { background-position: 1000px 0; }
                    }
                    .shimmer {
                        background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
                        background-size: 1000px 100%;
                        animation: shimmer 2s infinite;
                    }
                `}</style>

                <div style={{
                    maxWidth: '1400px',
                    margin: '0 auto',
                    background: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    padding: '12px',
                    position: 'relative'
                }}>
                    {/* Progress Bar */}
                    <LinearProgress
                        variant="determinate"
                        value={formProgress}
                        style={{ height: '3px', borderRadius: '4px 4px 0 0', background: '#e0e0e0', marginBottom: '10px' }}
                        sx={{
                            '& .MuiLinearProgress-bar': {
                                background: formProgress === 100
                                    ? 'linear-gradient(90deg, #11998e 0%, #38ef7d 100%)'
                                    : 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                                transition: 'all 0.5s ease'
                            }
                        }}
                    />

                    {/* Header */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '10px',
                        paddingBottom: '8px',
                        borderBottom: '1px solid #f0f0f0'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                padding: '8px',
                                borderRadius: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 4px 8px rgba(102, 126, 234, 0.3)'
                            }}>
                                <EditIcon style={{ fontSize: '22px', color: 'white' }} />
                            </div>
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <h1 style={{
                                        fontSize: '20px',
                                        fontWeight: '700',
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        margin: 0,
                                        letterSpacing: '-0.3px'
                                    }}>
                                        Edit User
                                    </h1>
                                    <Tooltip title="Update the required fields marked with *" arrow placement="right">
                                        <InfoIcon style={{ fontSize: '16px', color: '#667eea', cursor: 'pointer' }} />
                                    </Tooltip>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '4px' }}>
                                    <p style={{ margin: 0, color: '#666', fontSize: '11px' }}>Modify user information</p>
                                    <Chip
                                        icon={<CheckCircleIcon style={{ fontSize: '14px' }} />}
                                        label={`${formProgress}% Complete`}
                                        size="small"
                                        style={{
                                            height: '20px',
                                            fontSize: '10px',
                                            fontWeight: '600',
                                            background: formProgress === 100
                                                ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
                                                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            color: 'white'
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <Tooltip title="Save changes" arrow>
                                <span>
                                    <Button
                                        variant="contained"
                                        onClick={handleSubmit}
                                        disabled={isLoading}
                                        startIcon={isLoading ? null : <SaveIcon />}
                                        className="action-button-edit"
                                        style={{
                                            background: isLoading ? '#ccc' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            color: 'white',
                                            padding: '6px 16px',
                                            borderRadius: '8px',
                                            fontSize: '13px',
                                            boxShadow: isLoading ? 'none' : '0 2px 8px rgba(102, 126, 234, 0.3)',
                                            border: 'none',
                                            minHeight: '32px',
                                            position: 'relative',
                                            overflow: 'hidden'
                                        }}
                                    >
                                        {isLoading && (
                                            <div className="shimmer" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1 }} />
                                        )}
                                        <span style={{ position: 'relative', zIndex: 2 }}>
                                            {isLoading ? <CircularProgress size={16} color="inherit" /> : 'Update User'}
                                        </span>
                                    </Button>
                                </span>
                            </Tooltip>
                            <Tooltip title="Return to user list" arrow>
                                <Button
                                    variant="outlined"
                                    component={Link}
                                    to="/UserIndex"
                                    startIcon={<ArrowBackIcon />}
                                    className="action-button-edit"
                                    style={{
                                        borderColor: '#667eea',
                                        color: '#667eea',
                                        padding: '6px 16px',
                                        borderRadius: '8px',
                                        fontSize: '13px',
                                        borderWidth: '1.5px',
                                        minHeight: '32px'
                                    }}
                                >
                                    Back to List
                                </Button>
                            </Tooltip>
                        </div>
                    </div>

                    {/* Messages */}
                    {errorMessage && (
                        <div style={{
                            padding: '10px', marginBottom: '10px',
                            background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                            color: 'white', borderRadius: '8px', fontSize: '13px', fontWeight: '500'
                        }}>
                            {errorMessage}
                        </div>
                    )}
                    {successMessage && (
                        <div style={{
                            padding: '10px', marginBottom: '10px',
                            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                            color: 'white', borderRadius: '8px', fontSize: '13px', fontWeight: '500'
                        }}>
                            {successMessage}
                        </div>
                    )}

                    {/* Personal Information Section */}
                    <div className="section-card-edit" style={{
                        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                        padding: '10px', borderRadius: '10px', marginBottom: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)', position: 'relative', overflow: 'hidden'
                    }}>
                        {formData.FirstName && formData.LastName && (
                            <div style={{
                                position: 'absolute', top: '10px', right: '10px',
                                background: 'rgba(17, 153, 142, 0.9)', borderRadius: '50%',
                                padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                                <CheckCircleIcon style={{ fontSize: '16px', color: 'white' }} />
                            </div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <PersonIcon style={{ color: '#667eea', fontSize: '18px' }} />
                            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>Personal Information</h3>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                            {/* First Name */}
                            <div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: '600', color: '#4a5568', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                    <PersonIcon style={{ fontSize: '13px' }} /> First Name *
                                </label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        className="input-field-edit"
                                        value={formData.FirstName}
                                        name="FirstName"
                                        onChange={handleChange}
                                        type="text"
                                        placeholder="Enter first name"
                                        maxLength={30}
                                        style={{
                                            width: '100%', padding: '6px 10px', fontSize: '13px', borderRadius: '8px',
                                            border: formData.FirstName ? '1.5px solid #11998e' : '1.5px solid #e2e8f0',
                                            background: 'white', outline: 'none', fontWeight: '500'
                                        }}
                                    />
                                    {formData.FirstName && (
                                        <CheckCircleIcon style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px', color: '#11998e' }} />
                                    )}
                                </div>
                            </div>
                            {/* Last Name */}
                            <div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: '600', color: '#4a5568', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                    <PersonIcon style={{ fontSize: '13px' }} /> Last Name *
                                </label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        className="input-field-edit"
                                        value={formData.LastName}
                                        name="LastName"
                                        onChange={handleChange}
                                        type="text"
                                        placeholder="Enter last name"
                                        maxLength={30}
                                        style={{
                                            width: '100%', padding: '6px 10px', fontSize: '13px', borderRadius: '8px',
                                            border: formData.LastName ? '1.5px solid #11998e' : '1.5px solid #e2e8f0',
                                            background: 'white', outline: 'none', fontWeight: '500'
                                        }}
                                    />
                                    {formData.LastName && (
                                        <CheckCircleIcon style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px', color: '#11998e' }} />
                                    )}
                                </div>
                            </div>
                            {/* User Name */}
                            <div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: '600', color: '#4a5568', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                    <AccountCircleIcon style={{ fontSize: '13px' }} /> User Name (Auto-generated)
                                </label>
                                <input
                                    className="input-field-edit"
                                    value={formData.UserName}
                                    name="UserName"
                                    type="text"
                                    placeholder="Auto-generated"
                                    readOnly
                                    style={{
                                        width: '100%', padding: '6px 10px', fontSize: '13px', borderRadius: '8px',
                                        border: '1.5px solid #e2e8f0', background: '#fafafa', outline: 'none', cursor: 'not-allowed'
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Contact Information Section */}
                    <div className="section-card-edit" style={{
                        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                        padding: '10px', borderRadius: '10px', marginBottom: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)', position: 'relative', overflow: 'hidden'
                    }}>
                        {formData.ContactNumber && formData.Email && (
                            <div style={{
                                position: 'absolute', top: '10px', right: '10px',
                                background: 'rgba(17, 153, 142, 0.9)', borderRadius: '50%',
                                padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                                <CheckCircleIcon style={{ fontSize: '16px', color: 'white' }} />
                            </div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <PhoneIcon style={{ color: '#667eea', fontSize: '18px' }} />
                            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>Contact Details</h3>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                            {/* Contact Number */}
                            <div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: '600', color: '#4a5568', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                    <PhoneIcon style={{ fontSize: '13px' }} /> Contact Number *
                                </label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        className="input-field-edit"
                                        value={formData.ContactNumber}
                                        name="ContactNumber"
                                        onChange={handleChange}
                                        type="text"
                                        placeholder="Enter contact number"
                                        maxLength={15}
                                        style={{
                                            width: '100%', padding: '6px 10px', fontSize: '13px', borderRadius: '8px',
                                            border: formData.ContactNumber ? '1.5px solid #11998e' : '1.5px solid #e2e8f0',
                                            background: 'white', outline: 'none', fontWeight: '500'
                                        }}
                                    />
                                    {formData.ContactNumber && (
                                        <CheckCircleIcon style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px', color: '#11998e' }} />
                                    )}
                                </div>
                            </div>
                            {/* Email */}
                            <div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: '600', color: '#4a5568', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                    <EmailIcon style={{ fontSize: '13px' }} /> Email Address *
                                </label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        className="input-field-edit"
                                        value={formData.Email}
                                        name="Email"
                                        onChange={handleChange}
                                        type="text"
                                        placeholder="Enter email address"
                                        maxLength={40}
                                        style={{
                                            width: '100%', padding: '6px 10px', fontSize: '13px', borderRadius: '8px',
                                            border: formData.Email ? '1.5px solid #11998e' : '1.5px solid #e2e8f0',
                                            background: 'white', outline: 'none', fontWeight: '500'
                                        }}
                                    />
                                    {formData.Email && (
                                        <CheckCircleIcon style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px', color: '#11998e' }} />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fade>
    );
};

export default UserEdit;
